class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.handleViewCartClickBound = this.handleViewCartClick.bind(this);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.viewCartController?.abort();
    this.viewCartController = new AbortController();
    window.addEventListener('click', this.handleViewCartClickBound, {
      capture: true,
      signal: this.viewCartController.signal,
    });
    document.documentElement.classList.add('ew-native-cart-enabled');
    this.observeCompetingCart();
  }

  disconnectedCallback() {
    this.viewCartController?.abort();
    this.viewCartController = null;
    this.competingCartObserver?.disconnect();
    this.competingCartInsertionObserver?.disconnect();
    this.competingCartBodyObserver?.disconnect();
    this.clearFocusTrapSetup();
  }

  observeCompetingCart() {
    this.competingCartInsertionObserver?.disconnect();
    this.competingCartBodyObserver?.disconnect();
    this.bindCompetingCart(document.querySelector('#rebuy-cart'));
    this.competingCartInsertionObserver = new MutationObserver(() => {
      const nextCompetingCart = document.querySelector('#rebuy-cart');
      if (nextCompetingCart !== this.competingCart) this.bindCompetingCart(nextCompetingCart);
    });
    this.competingCartInsertionObserver.observe(document.body, { childList: true, subtree: true });
    this.competingCartBodyObserver = new MutationObserver(() => {
      if (
        document.body.classList.contains('rebuy-cart-visible') ||
        document.body.classList.contains('rebuy-modal-visible')
      ) {
        this.queueCompetingCartHide();
      }
    });
    this.competingCartBodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  bindCompetingCart(competingCart) {
    this.competingCartObserver?.disconnect();
    this.competingCart = competingCart;
    if (!competingCart) return;
    const wasOpen =
      document.body.classList.contains('rebuy-cart-visible') ||
      document.body.classList.contains('rebuy-modal-visible') ||
      competingCart.getAttribute('aria-hidden') === 'false';
    this.suppressCompetingCart();
    this.competingCartObserver = new MutationObserver(() => {
      const opened =
        document.body.classList.contains('rebuy-cart-visible') ||
        document.body.classList.contains('rebuy-modal-visible') ||
        competingCart.getAttribute('aria-hidden') === 'false';
      this.suppressCompetingCart();
      if (opened) this.queueCompetingCartHide();
    });
    this.competingCartObserver.observe(competingCart, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'style'],
    });
    if (wasOpen) this.queueCompetingCartHide();
  }

  suppressCompetingCart() {
    document.documentElement.classList.add('ew-native-cart-enabled');
    const competingCart = this.competingCart || document.querySelector('#rebuy-cart');
    if (!competingCart) return;
    if (competingCart.getAttribute('aria-hidden') !== 'true') competingCart.setAttribute('aria-hidden', 'true');
    if (!competingCart.hasAttribute('inert')) competingCart.setAttribute('inert', '');
  }

  hideCompetingCart() {
    const competingCart = this.competingCart || document.querySelector('#rebuy-cart');
    const wasOpen =
      document.body.classList.contains('rebuy-cart-visible') ||
      document.body.classList.contains('rebuy-modal-visible') ||
      competingCart?.getAttribute('aria-hidden') === 'false';
    this.suppressCompetingCart();
    if (wasOpen) {
      try {
        window.Rebuy?.SmartCart?.hide?.();
      } catch (_error) {
        this.suppressCompetingCart();
      }
    }
    document.body.classList.remove('rebuy-cart-visible', 'rebuy-modal-visible');
    if (this.classList.contains('active') && !this.contains(document.activeElement)) {
      window.requestAnimationFrame(() => {
        if (this.classList.contains('active') && !this.contains(document.activeElement)) this.activateFocusTrap();
      });
    }
  }

  queueCompetingCartHide() {
    if (this.competingCartHideQueued) return;
    this.competingCartHideQueued = true;
    window.requestAnimationFrame(() => {
      this.competingCartHideQueued = false;
      this.hideCompetingCart();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');
    cartLink.addEventListener('click', (event) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async handleViewCartClick(event) {
    const link = event.target instanceof Element
      ? event.target.closest('a.ew-drawer-view-cart')
      : null;
    if (!link || !this.contains(link)) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (link.target && link.target !== '_self') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      await customElements.get('cart-note')?.flush();
      this.close();
      window.location.assign(link.href);
    } catch (_error) {
      const note = this.querySelector('#CartDrawer-Note');
      const details = note?.closest('details');
      if (details) details.open = true;
      note?.focus();
    }
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    const focusReturnTarget = triggeredBy || (
      document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : document.querySelector('#cart-icon-bubble')
    );
    if (focusReturnTarget instanceof HTMLElement) this.setActiveElement(focusReturnTarget);
    this.hideCompetingCart();
    window.requestAnimationFrame(() => this.hideCompetingCart());
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.scheduleFocusTrap();
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    this.clearFocusTrapSetup();
    this.hideCompetingCart();
    document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  scheduleFocusTrap() {
    this.clearFocusTrapSetup();
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;

    const activateFocusTrap = () => this.activateFocusTrap();

    this.focusTrapTarget = panel;
    this.focusTrapTransitionHandler = (event) => {
      if (event.target === panel && event.propertyName === 'transform') activateFocusTrap();
    };
    panel.addEventListener('transitionend', this.focusTrapTransitionHandler);
    this.focusTrapTimer = window.setTimeout(activateFocusTrap, 400);
  }

  activateFocusTrap() {
    if (!this.classList.contains('active')) return;
    this.clearFocusTrapSetup();
    const containerToTrapFocusOn = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : document.getElementById('CartDrawer');
    const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
    if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
  }

  clearFocusTrapSetup() {
    window.clearTimeout(this.focusTrapTimer);
    if (this.focusTrapTarget && this.focusTrapTransitionHandler) {
      this.focusTrapTarget.removeEventListener('transitionend', this.focusTrapTransitionHandler);
    }
    this.focusTrapTimer = null;
    this.focusTrapTarget = null;
    this.focusTrapTransitionHandler = null;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  getRepaintFocusState(activeElement = document.activeElement) {
    if (!(activeElement instanceof HTMLElement) || !this.contains(activeElement)) return null;

    const lineItem = activeElement.closest('.cart-item');
    const quantityInput = lineItem?.querySelector('[data-quantity-line-key]');
    const lineKey = quantityInput?.dataset.quantityLineKey;
    if (lineKey) {
      if (activeElement === quantityInput) return { lineKey, control: 'quantity' };
      const controlName = activeElement.getAttribute('name');
      if (controlName === 'minus' || controlName === 'plus') return { lineKey, control: controlName };
      if (activeElement.matches('.cart-remove-button')) return { lineKey, control: 'remove' };
      if (activeElement.matches('.cart-item__name')) return { lineKey, control: 'name' };
    }

    if (activeElement.id) return { id: activeElement.id };
    if (activeElement.matches('.drawer__close')) return { selector: '.drawer__close' };
    if (activeElement.matches('.ew-drawer-view-cart')) return { selector: '.ew-drawer-view-cart' };

    if (activeElement.matches('summary')) {
      const details = activeElement.closest('details');
      if (details?.id) return { selector: `#${CSS.escape(details.id)} > summary` };
      if (details?.classList.contains('ew-drawer-multi-address')) {
        return { selector: '.ew-drawer-multi-address > summary' };
      }
    }

    return null;
  }

  resolveRepaintFocusState(focusState) {
    if (!focusState) return null;

    if (focusState.lineKey) {
      const quantityInput = [...this.querySelectorAll('[data-quantity-line-key]')].find(
        (input) => input.dataset.quantityLineKey === focusState.lineKey
      );
      if (!quantityInput) return null;
      if (focusState.control === 'quantity') return quantityInput;

      if (focusState.control === 'minus' || focusState.control === 'plus') {
        return quantityInput
          .closest('quantity-input')
          ?.querySelector(`[name="${focusState.control}"]`) ?? null;
      }

      const lineItem = quantityInput.closest('.cart-item');
      if (focusState.control === 'remove') return lineItem?.querySelector('.cart-remove-button') ?? null;
      if (focusState.control === 'name') return lineItem?.querySelector('.cart-item__name') ?? null;
      return null;
    }

    if (focusState.id) return this.querySelector(`#${CSS.escape(focusState.id)}`);
    if (focusState.selector) return this.querySelector(focusState.selector);
    return null;
  }

  captureRepaintState() {
    const note = this.querySelector('#CartDrawer-Note');
    const activeElement = document.activeElement;
    const noteWasFocused = note instanceof HTMLTextAreaElement && activeElement === note;

    return {
      hasNote: note instanceof HTMLTextAreaElement,
      noteDirty: note instanceof HTMLTextAreaElement && note.value !== note.defaultValue,
      noteValue: note instanceof HTMLTextAreaElement ? note.value : '',
      noteDetailsOpen: Boolean(note?.closest('details')?.open),
      noteWasFocused,
      noteSelectionStart: noteWasFocused ? note.selectionStart : null,
      noteSelectionEnd: noteWasFocused ? note.selectionEnd : null,
      noteSelectionDirection: noteWasFocused ? note.selectionDirection : null,
      focusState: this.getRepaintFocusState(activeElement),
    };
  }

  restoreRepaintState(state, { restoreFocus = false } = {}) {
    if (!state) return false;

    const note = this.querySelector('#CartDrawer-Note');
    if (state.hasNote && note instanceof HTMLTextAreaElement) {
      if (state.noteDirty) note.value = state.noteValue;
      const details = note.closest('details');
      if (details) details.open = state.noteDetailsOpen;
    }

    customElements.get('cart-note')?.sync();
    if (!restoreFocus) return false;

    const focusTarget = this.resolveRepaintFocusState(state.focusState);
    if (
      !(focusTarget instanceof HTMLElement) ||
      focusTarget.matches(':disabled, [aria-disabled="true"]')
    ) {
      return false;
    }

    const focusContainer = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : this.querySelector('#CartDrawer');
    if (!(focusContainer instanceof HTMLElement)) return false;

    trapFocus(focusContainer, focusTarget);

    if (
      state.noteWasFocused &&
      focusTarget === note &&
      Number.isInteger(state.noteSelectionStart) &&
      Number.isInteger(state.noteSelectionEnd)
    ) {
      note.setSelectionRange(
        state.noteSelectionStart,
        state.noteSelectionEnd,
        state.noteSelectionDirection || 'none'
      );
    }

    return document.activeElement === focusTarget;
  }

  renderContents(parsedState) {
    if (!parsedState || typeof parsedState.sections !== 'object' || parsedState.sections === null) {
      throw new Error('The cart response did not include rendered sections.');
    }

    const renderUpdates = this.getSectionsToRender()
      .map((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return null;

        const sectionHtml = parsedState.sections[section.id];
        if (typeof sectionHtml !== 'string') {
          throw new Error(`The cart response did not include ${section.id}.`);
        }

        const nextInnerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        if (nextInnerHTML === null) {
          throw new Error(`The cart response for ${section.id} was malformed.`);
        }

        return { sectionElement, nextInnerHTML };
      })
      .filter(Boolean);

    if (!renderUpdates.some((update) => update.sectionElement.id === 'CartDrawer')) {
      throw new Error('The cart response did not include the cart drawer.');
    }

    const drawerWasActive = this.classList.contains('active');
    const repaintState = this.captureRepaintState();
    if (drawerWasActive) {
      this.clearFocusTrapSetup();
      removeTrapFocus();
    }

    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    renderUpdates.forEach(({ sectionElement, nextInnerHTML }) => {
      sectionElement.innerHTML = nextInnerHTML;
    });

    const noteSummary = this.querySelector('[id^="Details-"] summary');
    if (noteSummary && !noteSummary.hasAttribute('role')) this.setSummaryAccessibility(noteSummary);
    this.restoreRepaintState(repaintState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (drawerWasActive) {
        if (!this.restoreRepaintState(repaintState, { restoreFocus: true })) this.activateFocusTrap();
      } else {
        this.open();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
